import React, { useContext, useEffect, useState } from 'react';
import './OrderDetails.css';
import { useTranslation } from 'react-i18next';
import { StoreContext } from '../../Context/StoreContext';
import { useMultiCart } from '../../Context/MultiCartContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import MapSelector from '../../components/MapSelector/MapSelector';
import { isWithinDeliveryArea } from '../../utils/locationUtils';

const OrderDetails = () => {
    const { t } = useTranslation();
    const [deliveryType, setDeliveryType] = useState("delivery");
    const [data, setData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        street: "",
        quartier: "",
        city: "",
        state: "",
        country: "",
        zipcode: "",
        phone: "",
        deliveryInstructions: "",
        coordinates: null
    });
    const [errors, setErrors] = useState({});
    const [showMap, setShowMap] = useState(false);
    const [isLocationValid, setIsLocationValid] = useState(false);
    const [locationWarning, setLocationWarning] = useState('');
    const [isCalculatingFee, setIsCalculatingFee] = useState(false);
    const [estimatedDeliveryFee, setEstimatedDeliveryFee] = useState(0);
    const [hasSelectedLocation, setHasSelectedLocation] = useState(false);
    const [isDeliveryInfoSaved, setIsDeliveryInfoSaved] = useState(false);
    const [isSavingDeliveryInfo, setIsSavingDeliveryInfo] = useState(false);
    const [lastSavedData, setLastSavedData] = useState(null);

    const { 
        getTotalCartAmount, 
        token, 
        food_list, 
        url, 
        currency, 
        deliveryCharge,
        setDeliveryCharge,
        selectedRestaurant,
        userAddress,
        userProfile,
        updateUserAddress,
        updateUserProfile,
        calculateDeliveryFee
    } = useContext(StoreContext);
    
    const { restaurantCarts } = useMultiCart();
    
    const navigate = useNavigate();
    const location = useLocation();
    
    // Check if we have restaurant cart data from multi-cart system
    const restaurantCart = location.state?.restaurantCart;
    const isMultiCartOrder = !!restaurantCart;

    // Helper function to get cart total for multi-cart orders
    const getMultiCartTotal = () => {
        if (!restaurantCart) return 0;
        return restaurantCart.subtotal || 0;
    };

    // Helper function to get the appropriate cart total
    const getCurrentCartTotal = () => {
        return isMultiCartOrder ? getMultiCartTotal() : getTotalCartAmount();
    };

    // Helper function to get current restaurant ID
    const getCurrentRestaurantId = () => {
        if (isMultiCartOrder) {
            return restaurantCart.restaurantId;
        } else {
            return selectedRestaurant?._id;
        }
    };

    // Helper function to get cart items for display
    const getCurrentCartItems = () => {
        if (isMultiCartOrder) {
            return restaurantCart.items || [];
        } else {
            // For the new two-step flow, we expect restaurant cart data
            // If no restaurant cart, redirect to cart page
            return [];
        }
    };

    // Redirect if no cart items
    useEffect(() => {
        // Don't redirect if we're coming from checkout with restaurant cart data
        if (location.state?.fromCheckout && restaurantCart) {
            return;
        }
        
        if (getCurrentCartTotal() === 0) {
            toast.error(t('orderDetails.cart_empty'));
            navigate('/cart');
        }
    }, [getCurrentCartTotal, navigate, location.state?.fromCheckout, restaurantCart]);

    // Initialize form data from user profile and address
    useEffect(() => {
        if (userProfile && userAddress) {
            setData(prevData => ({
                ...prevData,
                firstName: userProfile.firstName || userProfile.name?.split(' ')[0] || "",
                lastName: userProfile.lastName || userProfile.name?.split(' ').slice(1).join(' ') || "",
                email: userProfile.email || "",
                phone: userProfile.phone || "",
                street: userAddress.street || "",
                quartier: userAddress.quartier || "",
                city: userAddress.city || "Bujumbura",
                state: userAddress.state || "Bujumbura Mairie",
                country: userAddress.country || "Burundi",
                zipcode: userAddress.zipCode || "",
                deliveryInstructions: userAddress.deliveryInstructions || "",
                coordinates: userAddress.coordinates || null
            }));

            // If we have coordinates, set location state and mark as saved (existing user data)
            if (userAddress.coordinates) {
                const isValid = isWithinDeliveryArea(userAddress.coordinates.latitude, userAddress.coordinates.longitude);
                setIsLocationValid(isValid);
                setHasSelectedLocation(true);
                setIsDeliveryInfoSaved(true); // Mark existing user data as saved
                
                // Store existing data as last saved
                setLastSavedData({
                    deliveryType,
                    firstName: userProfile?.firstName || userProfile?.name?.split(' ')[0] || "",
                    lastName: userProfile?.lastName || userProfile?.name?.split(' ').slice(1).join(' ') || "",
                    email: userProfile?.email || "",
                    phone: userProfile?.phone || "",
                    street: userAddress.street || "",
                    quartier: userAddress.quartier || "",
                    city: userAddress.city || "Bujumbura",
                    state: userAddress.state || "Bujumbura Mairie",
                    country: userAddress.country || "Burundi",
                    zipcode: userAddress.zipCode || "",
                    deliveryInstructions: userAddress.deliveryInstructions || "",
                    coordinates: userAddress.coordinates
                });
                
                if (deliveryType === 'delivery') {
                    calculateDeliveryFeeForCoordinates(userAddress.coordinates);
                }
            }
        }
    }, [userProfile, userAddress, deliveryType]);

    const onChangeHandler = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setData(data => ({ ...data, [name]: value }));
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Calculate delivery fee for given coordinates
    const calculateDeliveryFeeForCoordinates = async (coordinates) => {
        const restaurantId = getCurrentRestaurantId();
        
        if (!restaurantId || !coordinates || deliveryType !== 'delivery') {
            setEstimatedDeliveryFee(0);
            setDeliveryCharge(0);
            return;
        }

        setIsCalculatingFee(true);

        try {
            const customerAddress = {
                ...data,
                coordinates
            };

            const fee = await calculateDeliveryFee(restaurantId, customerAddress);
            setEstimatedDeliveryFee(fee);
            setDeliveryCharge(fee);
        } catch (error) {
            console.error('Error calculating delivery fee:', error);
            toast.error(t('orderDetails.failed_calculate_delivery_fee'));
            setEstimatedDeliveryFee(1000); // Default fee
            setDeliveryCharge(1000);
        } finally {
            setIsCalculatingFee(false);
        }
    };

    const handleLocationSelect = async (position) => {
        const isValid = isWithinDeliveryArea(position.lat, position.lng);
        
        const coordinates = {
            latitude: position.lat,
            longitude: position.lng
        };
        
        setData(data => ({ 
            ...data, 
            coordinates
        }));
        
        setIsLocationValid(isValid);
        setHasSelectedLocation(true); // Mark that user has interacted with the map
        
        if (!isValid) {
            setLocationWarning(t('orderDetails.location_outside_delivery_area'));
            setTimeout(() => setLocationWarning(''), 8000);
        } else {
            setLocationWarning('');
        }
    };

    // Manual save delivery information
    const saveDeliveryInfo = async () => {
        if (!token) return;
        
        // For delivery orders, coordinates are required
        if (deliveryType === 'delivery' && !data.coordinates) return;

        setIsSavingDeliveryInfo(true);
        try {
            // Update user profile (including phone number)
            const profileData = {
                name: `${data.firstName} ${data.lastName}`.trim(),
                email: data.email,
                phone: data.phone
            };

            // Update address data
            const addressData = {
                street: data.street,
                quartier: data.quartier,
                city: data.city,
                state: data.state,
                country: data.country,
                zipCode: data.zipcode,
                deliveryInstructions: data.deliveryInstructions,
                coordinates: deliveryType === 'delivery' ? data.coordinates : null
            };

            // Save both profile and address
            await Promise.all([
                updateUserProfile(profileData),
                updateUserAddress(addressData)
            ]);

            setIsDeliveryInfoSaved(true);
            
            // Store the saved data for comparison
            setLastSavedData({
                deliveryType,
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phone: data.phone,
                street: data.street,
                quartier: data.quartier,
                city: data.city,
                state: data.state,
                country: data.country,
                zipcode: data.zipcode,
                deliveryInstructions: data.deliveryInstructions,
                coordinates: data.coordinates
            });
            
            // Calculate delivery fee after saving (only for delivery orders)
            if (deliveryType === 'delivery' && data.coordinates) {
                await calculateDeliveryFeeForCoordinates(data.coordinates);
            }
            
            toast.success(t('orderDetails.information_saved', { type: deliveryType === 'delivery' ? t('orderDetails.delivery') : t('orderDetails.customer') }));
        } catch (error) {
            console.error('Failed to save delivery info:', error);
            toast.error(t('orderDetails.failed_save_information', { type: deliveryType === 'delivery' ? t('orderDetails.delivery') : t('orderDetails.customer') }));
        } finally {
            setIsSavingDeliveryInfo(false);
        }
    };

    // Reset saved state when delivery info changes (but not if it's the same as last saved)
    useEffect(() => {
        if (!lastSavedData) return;
        
        const currentData = {
            deliveryType,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phone: data.phone,
            street: data.street,
            quartier: data.quartier,
            city: data.city,
            state: data.state,
            country: data.country,
            zipcode: data.zipcode,
            deliveryInstructions: data.deliveryInstructions,
            coordinates: data.coordinates
        };
        
        // Check if current data is different from last saved data
        const hasChanged = JSON.stringify(currentData) !== JSON.stringify(lastSavedData);
        
        if (hasChanged && isDeliveryInfoSaved) {
            setIsDeliveryInfoSaved(false);
        }
    }, [deliveryType, data.firstName, data.lastName, data.email, data.phone, data.street, data.quartier, data.city, data.state, data.country, data.zipcode, data.deliveryInstructions, data.coordinates, lastSavedData, isDeliveryInfoSaved]);

    const validateForm = () => {
        const newErrors = {};

        if (!data.firstName.trim()) newErrors.firstName = t('orderDetails.first_name_required');
        if (!data.lastName.trim()) newErrors.lastName = t('orderDetails.last_name_required');
        if (!data.email.trim()) newErrors.email = t('orderDetails.email_required');
        if (!data.phone.trim()) newErrors.phone = t('orderDetails.phone_number_required');

        if (deliveryType === 'delivery') {
            if (!data.street.trim()) newErrors.street = t('orderDetails.street_address_required');
            if (!data.quartier.trim()) newErrors.quartier = t('orderDetails.quartier_required');
            if (!data.city.trim()) newErrors.city = t('orderDetails.city_required');
            if (!data.state.trim()) newErrors.state = t('orderDetails.state_required');
            if (!data.country.trim()) newErrors.country = t('orderDetails.country_required');
            if (!hasSelectedLocation || !data.coordinates) newErrors.coordinates = t('orderDetails.please_select_delivery_location');
            if (data.coordinates && !isLocationValid) newErrors.coordinates = ''; // Clear previous warning
            if (!isDeliveryInfoSaved) newErrors.deliveryInfo = t('orderDetails.please_save_delivery_info');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleContinueToPayment = () => {
        if (!validateForm()) {
            toast.error(t('orderDetails.fill_required_fields'));
            return;
        }

        // Save order details to session storage for Step 2
        const orderDetails = {
            deliveryType,
            customerInfo: data,
            deliveryFee: deliveryType === 'delivery' ? estimatedDeliveryFee : 0,
            restaurantCart: isMultiCartOrder ? restaurantCart : null,
            restaurantId: getCurrentRestaurantId()
        };

        sessionStorage.setItem('orderDetails', JSON.stringify(orderDetails));

        // Navigate to payment step
        navigate('/checkout', { 
            state: { 
                fromOrderDetails: true,
                orderDetails 
            } 
        });
    };

    const cartItems = getCurrentCartItems();
    const subtotal = getCurrentCartTotal();
    const deliveryFee = deliveryType === 'delivery' ? estimatedDeliveryFee : 0;
    const total = subtotal + deliveryFee;

    return (
        <div className='order-details'>
            <div className="order-details-container">
                <div className="order-details-left">
                    <h2>{t('orderDetails.order_details')}</h2>
                    
                    {/* Order Type Selection */}
                    <div className="order-type-section">
                        <h3>{t('orderDetails.order_type')}</h3>
                        <div className="order-type-buttons">
                            <button 
                                type="button"
                                className={`order-type-btn ${deliveryType === 'delivery' ? 'active' : ''}`}
                                onClick={() => {
                                    setDeliveryType('delivery');
                                    if (data.coordinates) {
                                        calculateDeliveryFeeForCoordinates(data.coordinates);
                                    }
                                }}
                            >
                                🚚 {t('orderDetails.delivery')}
                            </button>
                            <button 
                                type="button"
                                className={`order-type-btn ${deliveryType === 'pickup' ? 'active' : ''}`}
                                onClick={() => {
                                    setDeliveryType('pickup');
                                    setEstimatedDeliveryFee(0);
                                    setDeliveryCharge(0);
                                }}
                            >
                                🏪 {t('orderDetails.pickup')}
                            </button>
                        </div>
                    </div>

                    {/* Customer Information */}
                    <div className="customer-info-section">
                        <h3>{t('orderDetails.customer_information')}</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <input 
                                    name="firstName" 
                                    onChange={onChangeHandler} 
                                    value={data.firstName} 
                                    type="text" 
                                    placeholder={t('orderDetails.first_name')}
                                    className={errors.firstName ? 'error' : ''}
                                />
                                {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                            </div>
                            <div className="form-group">
                                <input 
                                    name="lastName" 
                                    onChange={onChangeHandler} 
                                    value={data.lastName} 
                                    type="text" 
                                    placeholder={t('orderDetails.last_name')}
                                    className={errors.lastName ? 'error' : ''}
                                />
                                {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                            </div>
                        </div>
                        <div className="form-group">
                            <input 
                                name="email" 
                                onChange={onChangeHandler} 
                                value={data.email} 
                                type="email" 
                                placeholder={t('orderDetails.email_address')}
                                className={errors.email ? 'error' : ''}
                            />
                            {errors.email && <span className="error-text">{errors.email}</span>}
                        </div>
                        <div className="form-group">
                            <input 
                                name="phone" 
                                onChange={onChangeHandler} 
                                value={data.phone} 
                                type="text" 
                                placeholder={t('orderDetails.phone_number')}
                                className={errors.phone ? 'error' : ''}
                            />
                            {errors.phone && <span className="error-text">{errors.phone}</span>}
                        </div>
                    </div>

                    {/* Delivery Information (only for delivery orders) */}
                    {deliveryType === 'delivery' && (
                        <div className="delivery-info-section">
                            <h3>{t('orderDetails.delivery_information')}</h3>
                            <div className="form-group">
                                <input 
                                    name="street" 
                                    onChange={onChangeHandler} 
                                    value={data.street} 
                                    type="text" 
                                    placeholder={t('orderDetails.street_address')}
                                    className={errors.street ? 'error' : ''}
                                />
                                {errors.street && <span className="error-text">{errors.street}</span>}
                            </div>
                            <div className="form-group">
                                <input 
                                    name="quartier" 
                                    onChange={onChangeHandler} 
                                    value={data.quartier} 
                                    type="text" 
                                    placeholder={t('orderDetails.quartier')}
                                    className={errors.quartier ? 'error' : ''}
                                />
                                {errors.quartier && <span className="error-text">{errors.quartier}</span>}
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <input 
                                        name="city" 
                                        onChange={onChangeHandler} 
                                        value={data.city} 
                                        type="text" 
                                        placeholder={t('orderDetails.city')}
                                        className={errors.city ? 'error' : ''}
                                    />
                                    {errors.city && <span className="error-text">{errors.city}</span>}
                                </div>
                                <div className="form-group">
                                    <input 
                                        name="state" 
                                        onChange={onChangeHandler} 
                                        value={data.state} 
                                        type="text" 
                                        placeholder={t('orderDetails.state')}
                                        className={errors.state ? 'error' : ''}
                                    />
                                    {errors.state && <span className="error-text">{errors.state}</span>}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <input 
                                        name="country" 
                                        onChange={onChangeHandler} 
                                        value={data.country} 
                                        type="text" 
                                        placeholder={t('orderDetails.country')}
                                        className={errors.country ? 'error' : ''}
                                    />
                                    {errors.country && <span className="error-text">{errors.country}</span>}
                                </div>
                                <div className="form-group">
                                    <input 
                                        name="zipcode" 
                                        onChange={onChangeHandler} 
                                        value={data.zipcode} 
                                        type="text" 
                                        placeholder={t('orderDetails.zip_code_optional')}
                                    />
                                </div>
                            </div>
                            
                            {/* Map Selection */}
                            <div className="map-section">
                                <div className="map-header">
                                    <h4>{t('orderDetails.select_delivery_location')}</h4>
                                    <button 
                                        type="button"
                                        className="map-toggle-btn"
                                        onClick={() => setShowMap(!showMap)}
                                    >
                                        {showMap ? t('orderDetails.hide_map') : t('orderDetails.show_map')}
                                    </button>
                                </div>
                                
                                {showMap && (
                                    <div className={`map-container ${deliveryType === 'delivery' && !hasSelectedLocation ? 'required' : ''}`}>
                                        <MapSelector 
                                            onLocationSelect={handleLocationSelect}
                                            initialPosition={data.coordinates}
                                        />
                                    </div>
                                )}
                                
                                {errors.coordinates && <span className="error-text">{errors.coordinates}</span>}
                                {locationWarning && <div className="warning-text">{locationWarning}</div>}
                                
                                {hasSelectedLocation && data.coordinates && isLocationValid && (
                                    <div className="location-info">
                                        <span className="location-confirmed">{t('orderDetails.location_confirmed')}</span>
                                    </div>
                                )}
                            </div>
                            
                            <div className="form-group">
                                <textarea 
                                    name="deliveryInstructions" 
                                    onChange={onChangeHandler} 
                                    value={data.deliveryInstructions} 
                                    placeholder={t('orderDetails.delivery_instructions_optional')}
                                    rows="3"
                                />
                            </div>
                            
                            {/* Save Delivery Information Button */}
                            <div className="save-delivery-section">
                                <button 
                                    type="button"
                                    className={`save-delivery-btn ${isDeliveryInfoSaved ? 'saved' : ''}`}
                                    onClick={saveDeliveryInfo}
                                    disabled={
                                        (deliveryType === 'delivery' && (!hasSelectedLocation || !isLocationValid)) || 
                                        isSavingDeliveryInfo || 
                                        isDeliveryInfoSaved
                                    }
                                >
                                    {isSavingDeliveryInfo ? t('orderDetails.saving') : isDeliveryInfoSaved ? t('orderDetails.delivery_info_saved') : t('orderDetails.save_delivery_information')}
                                </button>
                                
                                {errors.deliveryInfo && <span className="error-text">{errors.deliveryInfo}</span>}
                                
                                {isDeliveryInfoSaved && estimatedDeliveryFee > 0 && (
                                    <div className="delivery-fee-info">
                                        <span className="delivery-fee-preview">
                                            {t('orderDetails.estimated_delivery_fee')}: {estimatedDeliveryFee} {currency}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="order-details-right">
                    <div className="order-summary">
                        <h3>{t('orderDetails.order_summary')}</h3>
                        
                        {/* Cart Items */}
                        <div className="cart-items">
                            {cartItems.map((item, index) => (
                                <div key={index} className="cart-item">
                                    <div className="item-details">
                                        <span className="item-name">{item.name}</span>
                                        <span className="item-quantity">x{item.quantity}</span>
                                    </div>
                                    <span className="item-total">{Math.round(item.price * item.quantity)} {currency}</span>
                                </div>
                            ))}
                        </div>

                        {/* Order Totals */}
                        <div className="order-totals">
                            <div className="total-row">
                                <span>{t('orderDetails.subtotal')}</span>
                                <span>{Math.round(subtotal)} {currency}</span>
                            </div>
                            <div className="total-row">
                                <span>{deliveryType === 'delivery' ? t('orderDetails.delivery_fee') : t('orderDetails.pickup_fee')}</span>
                                <span>
                                    {isCalculatingFee ? t('orderDetails.calculating') : `${Math.round(deliveryFee)} ${currency}`}
                                </span>
                            </div>
                            <hr />
                            <div className="total-row total">
                                <span><strong>{t('orderDetails.total')}</strong></span>
                                <span><strong>{Math.round(total)} {currency}</strong></span>
                            </div>
                        </div>

                        {/* Continue Button */}
                        <button 
                            className="continue-btn"
                            onClick={handleContinueToPayment}
                            disabled={isCalculatingFee}
                        >
                            {isCalculatingFee ? t('orderDetails.calculating') : t('orderDetails.continue_to_payment')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetails; 